import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Avatar, message, Space, Typography } from 'antd';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import EmojiPicker from 'emoji-picker-react';
import { MouseDownEvent } from 'emoji-picker-react/dist/config/config';
import { FaRegKeyboard } from 'react-icons/fa6';
import { IoCloseCircle } from 'react-icons/io5';
import { PiCamera, PiPaperPlaneRightFill, PiSmileyLight } from 'react-icons/pi';
import {
  Mention,
  MentionsInput,
  MentionsInputProps,
  SuggestionDataItem,
} from 'react-mentions';
import { useParams } from 'react-router-dom';
import { BottomSheet } from 'react-spring-bottom-sheet';

import AnalyticsEventsAPI from '../../../../api/AnalyticsEventsAPI';
import CommunityAPI from '../../../../api/CommunityAPI';
import {
  addCommunityPostComments,
  appendReplyComments,
  setCommentToEdit,
  setReplyToComment,
  updateCommunitComments,
  updateReplyComment,
} from '../../../../context/communityReducer';
import { loadMentionedUsers } from '../../../../shared/communityUtils';
import showAppError from '../../../../shared/error';
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks';
import { convertImage, getFileExtension } from '../../../../shared/utils';
import { IMentionedUser } from '../../../../types/commentTypes';
import { ICommentReqBody } from '../../../../types/communityTypes';
import { PlatformIntegrationPlatformEnum } from '../../../../types/integrationType';
import ReplyCommentCard from './ReplyCommentCard';
import { EUploadModuleType } from '../../../../types/fileTypes';

type Props = {};

const CommunityPostCommentInput: React.FC<Props> = ({}) => {
  const dispatch = useAppDispatch();
  const { replyComment, commentToEdit, pdpCommunityPostData } = useAppSelector(
    (state) => state?.community,
  );
  const userDetails = useAppSelector((state) => state.user);

  const { channelId, postId } = useParams();
  const textareaRef = useRef<TextAreaRef | null>(null);
  const mentionsRef = useRef<React.Component<MentionsInputProps>>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const suggestionContainerRef = useRef<HTMLDivElement>(null);

  const touchedRef = useRef<boolean>(false);

  const [comment, setComment] = useState(commentToEdit?.comment || '');
  const [openEmoji, setOpenEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emptySearchResults, setEmptySearchResults] = useState<string[]>([]);
  const [file, setFile] = useState<{ file: File; url: string } | null>(null);

  const closeModal = () => {
    setOpenEmoji(false);
  };

  const resetStates = () => {
    setComment('');
    setOpenEmoji(false);
    setFile(null);
  };

  const beforeUpload = (fileData: File) => {
    const type = getFileExtension(fileData.name)?.[1].toLowerCase();
    const isImage =
      type === 'jpg' ||
      type === 'png' ||
      type === 'gif' ||
      type === 'webp' ||
      type === 'tiff' ||
      type === 'jpeg' ||
      type === 'heic' ||
      type === 'svg';
    if (!isImage) {
      message.error('You can only put a valid image here!');
      return false;
    }
    convertImage(type, fileData, (rowFile, url) =>
      setFile({ file: rowFile, url }),
    );
  };

  const onSelect = useCallback(async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    // input.setAttribute('multiple', true as unknown as string);
    input.setAttribute(
      'accept',
      '.jpg, .jpeg, .png, .svg, .gif, .bmp, .webp, .tiff, .heic',
    );
    input.click();
    input.addEventListener('change', (e: any) => {
      let evt: React.ChangeEvent<HTMLInputElement> = e as any;
      if (!evt.target?.files) return;
      beforeUpload(e.target.files[0] as File);
    });
  }, []);

  const addedOnIcons = useMemo(
    () => (
      <div className="addon-button_wrapper">
        <button
          className="addon-button_wrapper_button"
          disabled={loading}
          onClick={() => {
            if (openEmoji) {
              textareaRef.current?.focus();
            }
            setOpenEmoji((prev) => !prev);
          }}>
          {openEmoji ? <FaRegKeyboard /> : <PiSmileyLight />}
        </button>

        <button
          disabled={loading}
          onClick={() => {
            onSelect();
          }}
          style={{ marginRight: 2 }}
          className="addon-button_wrapper_button">
          <PiCamera />
        </button>
      </div>
    ),
    [loading, onSelect, openEmoji],
  );

  const onSelectEmoji = useCallback((emoji: string) => {
    if (!textareaRef.current) return;

    const input = textareaRef.current;

    const textarea = input?.resizableTextArea?.textArea;

    if (!textarea) return;

    let selectionStart = textarea?.selectionStart;
    let selectionEnd = textarea?.selectionEnd;

    if (selectionStart === null || selectionEnd === null) return;

    if (
      selectionStart === 0 &&
      selectionEnd === 0 &&
      textarea?.textLength > 0
    ) {
      selectionStart = textarea?.textLength;
      selectionEnd = textarea?.textLength;
    }
    setComment(
      (prev) =>
        prev.slice(0, selectionStart) + emoji + prev.slice(selectionEnd),
    );
    const newCursor = selectionStart + emoji.length;
    setTimeout(() => textarea.setSelectionRange(newCursor, newCursor), 10);
    textarea.focus();
  }, []);

  const handleOnEmojiClick: MouseDownEvent = (event) => {
    onSelectEmoji(event?.emoji);
  };

  const searchMentionedUser = async (search: string) => {
    if (!channelId) return [];
    const resp = await loadMentionedUsers(channelId, search);
    return resp;
  };

  const onMentionSearch = async (
    searchText: string,
    callback: (mentions: IMentionedUser[]) => void,
  ) => {
    if (!searchMentionedUser) {
      callback([]);
      return;
    }

    if (searchText.length === 0) {
      callback([]);
      return;
    }

    if (emptySearchResults.find((item) => searchText.startsWith(item))) {
      callback([]);
      // eslint-disable-next-line no-useless-return
      return;
    }

    try {
      const result = await searchMentionedUser(searchText);

      if (result.length === 0) {
        setEmptySearchResults((prev) => [...prev, searchText]);
      }

      callback(
        result.map((user) => ({
          id: user.id,
          display: user.display,
          profilePicUrl: user.profilePicUrl,
        })),
      );
    } catch (error) {
      showAppError(error);
    }
  };

  const updateComment = useCallback(async () => {
    if (!commentToEdit?._id) return;
    if (!channelId || !postId) return;
    if (!comment.trim()) {
      message.warning('Type a message to edit');
      return;
    }
    try {
      setLoading(true);
      const commentReq: ICommentReqBody = {
        comment,
      };
      const resp = await CommunityAPI.updateComment(
        channelId,
        postId,
        commentToEdit?._id,
        commentReq,
      );
      if (resp?.status === 200) {
        dispatch(setCommentToEdit(null));
        if (commentToEdit?.isReplay) {
          dispatch(
            updateReplyComment({
              parentId: commentToEdit?.parentId,
              comment: {
                ...resp?.data?.result,
                commentedBy: commentToEdit?.commentedBy,
              },
            }),
          );
        } else {
          dispatch(
            updateCommunitComments({
              ...resp?.data?.result,
              commentedBy: commentToEdit?.commentedBy,
            }),
          );
        }
        resetStates();
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setLoading(false);
    }
  }, [
    commentToEdit?._id,
    commentToEdit?.isReplay,
    commentToEdit?.parentId,
    commentToEdit?.commentedBy,
    channelId,
    postId,
    comment,
    dispatch,
  ]);

  const addComment = useCallback(async () => {
    if (!(channelId && postId)) return;
    if (!comment.trim() && !file) {
      message.warning('Write comment...');
      return;
    }
    try {
      setLoading(true);
      const commentReq: ICommentReqBody = {
        comment,
      };
      if (replyComment) {
        commentReq.repliedComment = replyComment?._id;
      }
      if (file) {
        const respImg = await CommunityAPI.uploadCommunityMedia(
          [file?.file],
          () => {},
          EUploadModuleType.COMMUNITY_REPLY,
        );
        if (respImg?.status === 201 && respImg?.data?.result?.assets?.length) {
          commentReq.images = respImg?.data?.result.assets?.map(
            (eachMedia) => eachMedia?.url,
          );
        }
      }
      const resp = await CommunityAPI.crateComment(
        channelId,
        postId,
        commentReq,
      );
      if (resp?.status === 201) {
        if (replyComment?.parentCommentId || replyComment?._id) {
          if (replyComment?.viewReply) {
            dispatch(
              appendReplyComments({
                comment: resp?.data?.result,
                parentId: replyComment?.parentCommentId || replyComment?._id,
              }),
            );
          } else {
            dispatch(
              updateCommunitComments({ ...replyComment, viewReply: true }),
            );
          }
          const postDetails = pdpCommunityPostData;
          try {
            AnalyticsEventsAPI.trackCommunityEvents('POST_COMMENTED', {
              user_id: userDetails.id,
              onboarding: userDetails.type,
              name: userDetails.name,
              email: userDetails.email,
              phone_number: userDetails.phone,
              host_commenter: userDetails.host,
              platform_commenter: PlatformIntegrationPlatformEnum.MOBILE,
              post_creator_id: postDetails?.postedBy,
              post_id: postId,
              comment,
              timestamp: new Date().toISOString(),
            });
          } catch (error: any) {
            console.log('Error in tracking post commented events:', error);
          }
        } else {
          dispatch(addCommunityPostComments(resp?.data?.result));
        }
        resetStates();
        if (replyComment) {
          dispatch(setReplyToComment(null));
        }
        // After successfully adding the comment, wait 1 second, then scroll to the new comment in the DOM if possible
        if (resp?.data?.result?._id) {
          setTimeout(() => {
            const commentElement = document.getElementById(
              resp.data.result._id,
            );
            if (commentElement) {
              commentElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }, 1000);
        }
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setLoading(false);
    }
  }, [
    channelId,
    comment,
    dispatch,
    file,
    postId,
    replyComment,
    pdpCommunityPostData,
    userDetails,
  ]);

  const displayUploadedImg = useCallback(() => {
    if (file?.url) {
      return (
        <div className="comment-input_container_media">
          <img
            className="comment-input_container_media_uploaded-img"
            src={file?.url}
          />
          <button
            className="siteBtn noStyle comment-input_container_media_cross-btn"
            onClick={() => setFile(null)}>
            <IoCloseCircle />
          </button>
        </div>
      );
    }
    return null;
  }, [file?.url]);

  const handleSend = useCallback(() => {
    const textValue = comment.trim();

    // if (!textValue) return;

    if (!textValue && !file && !commentToEdit) {
      message.warning('Type a message or upload a file to send');
      return;
    }

    if (commentToEdit && !textValue) {
      message.warning('Type a message to edit');
      return;
    }

    if (commentToEdit) {
      updateComment();
    } else {
      addComment();
    }
  }, [addComment, comment, commentToEdit, file, updateComment]);

  useEffect(() => {
    if (!openEmoji && touchedRef.current) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 800);
    }
  }, [openEmoji]);

  useEffect(() => {
    if (replyComment?._id) {
      setComment(
        `{{${replyComment?.replyTo?._id}#${replyComment?.replyTo?.name}}}`,
      );
    } else {
      setComment('');
    }
  }, [replyComment]);

  useEffect(() => {
    if (commentToEdit) {
      setComment(commentToEdit?.comment);
    }
  }, [commentToEdit]);

  return (
    <>
      <div className="comment-input_container">
        {replyComment || commentToEdit ? (
          <ReplyCommentCard
            loading={loading}
            replyComment={replyComment || commentToEdit}
            isEditMode={Boolean(commentToEdit)}
            resetStates={resetStates}
          />
        ) : null}
        {openEmoji ? null : (
          <div className="comment-input_container_">
            <div className="comment-input_container__input">
              <MentionsInput
                id={`input${channelId}-${postId} ?? ''}`}
                className="chat-input__box"
                style={{ width: '100%' }}
                disabled={loading}
                placeholder={'Type a message'}
                singleLine={false}
                allowSpaceInQuery
                ref={mentionsRef}
                inputRef={inputRef}
                suggestionsPortalHost={
                  suggestionContainerRef.current ?? undefined
                }
                allowSuggestionsAboveCursor
                forceSuggestionsAboveCursor
                value={comment}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(_: any, value: string) => {
                  setComment(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}>
                <Mention
                  trigger="@"
                  data={onMentionSearch}
                  appendSpaceOnAdd
                  markup="{{__id__#__display__}}"
                  displayTransform={(_, display) => `@${display}`}
                  renderSuggestion={(
                    suggestion: SuggestionDataItem,
                    _: string,
                    highlightedDisplay: React.ReactNode,
                  ) => (
                    <div className="mention-suggestion">
                      <Space size={8}>
                        <Avatar
                          src={(suggestion as IMentionedUser).profilePicUrl}
                          alt="image"
                          size={32}
                          style={{ flexShrink: 0 }}
                        />
                        <Typography.Text className="mention-suggestion__name">
                          {highlightedDisplay}
                        </Typography.Text>
                      </Space>
                    </div>
                  )}
                />
              </MentionsInput>
              {addedOnIcons}
            </div>
            <div className="comment-input_container__send-button">
              <button disabled={loading} onClick={handleSend}>
                <PiPaperPlaneRightFill />
              </button>
            </div>
          </div>
        )}
        {displayUploadedImg()}
      </div>
      {openEmoji && (
        <BottomSheet
          open={openEmoji}
          onDismiss={() => closeModal()}
          style={{ zIndex: 0 }}>
          <>
            {openEmoji ? (
              <div className="comment-input_container">
                <div className="comment-input_container_">
                  <MentionsInput
                    id={`input${channelId}-${postId} ?? ''}`}
                    className="chat-input__box"
                    style={{ width: '100%' }}
                    disabled={loading}
                    placeholder={'Type a message'}
                    singleLine={false}
                    allowSpaceInQuery
                    ref={mentionsRef}
                    inputRef={inputRef}
                    suggestionsPortalHost={
                      suggestionContainerRef.current ?? undefined
                    }
                    allowSuggestionsAboveCursor
                    forceSuggestionsAboveCursor
                    value={comment}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(_: any, value: string) => {
                      setComment(value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}>
                    <Mention
                      trigger="@"
                      data={onMentionSearch}
                      appendSpaceOnAdd
                      markup="{{__id__#__display__}}"
                      displayTransform={(_, display) => `@${display}`}
                      renderSuggestion={(
                        suggestion: SuggestionDataItem,
                        _: string,
                        highlightedDisplay: React.ReactNode,
                      ) => (
                        <div className="mention-suggestion">
                          <Space size={8}>
                            <Avatar
                              src={(suggestion as IMentionedUser).profilePicUrl}
                              alt="image"
                              size={32}
                              style={{ flexShrink: 0 }}
                            />
                            <Typography.Text className="mention-suggestion__name">
                              {highlightedDisplay}
                            </Typography.Text>
                          </Space>
                        </div>
                      )}
                    />
                  </MentionsInput>
                  {addedOnIcons}
                  <div className="comment-input_container__send-button">
                    <button disabled={loading} onClick={handleSend}>
                      <PiPaperPlaneRightFill />
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </>
          <EmojiPicker
            className="emoji_container_wrapper"
            onEmojiClick={handleOnEmojiClick}
            autoFocusSearch={false}
            lazyLoadEmojis
          />
        </BottomSheet>
      )}
    </>
  );
};

export default memo(CommunityPostCommentInput);
